/* ================================================================
   NewJaisa — Student Experience
   File    : assets/student-cookie.js
   Purpose : Student session storage ONLY (read / write / clear).
   Exposes : window.NJStudentCookie

   ARCHITECTURE FREEZE (2026-07-10): first-party cookies, 48-hour
   expiry. NOT localStorage, NOT sessionStorage, NOT 30 days.
     njc_student          = "true"   (Condition 2: session exists)
     college_code         = "<code>" (which of the 6 colleges)
     njc_student_landing  = "1"      (Condition 1: only ever set by
                                       a student landing page, so its
                                       presence proves entry origin —
                                       tracked separately from the
                                       session flag so the debug
                                       panel can report them as the
                                       two distinct conditions they are)
   All three cookies share the same 48h expiry and are cleared together.
   ================================================================ */

window.NJStudentCookie = (function () {
  'use strict';

  var STUDENT_FLAG_COOKIE = 'njc_student';
  var COLLEGE_CODE_COOKIE = 'college_code';
  var LANDING_COOKIE = 'njc_student_landing';
  var COOKIE_HOURS = 48;
  var COOKIE_PATH = '/';

  function buildAttributes(maxAgeSeconds) {
    var attrs = [
      'path=' + COOKIE_PATH,
      'max-age=' + maxAgeSeconds,
      'SameSite=Lax'
    ];

    if (window.location.protocol === 'https:') {
      attrs.push('Secure');
    }

    return attrs.join('; ');
  }

  function readRawCookie(name) {
    var cookies = document.cookie ? document.cookie.split('; ') : [];

    for (var i = 0; i < cookies.length; i++) {
      var parts = cookies[i].split('=');
      var key = decodeURIComponent(parts.shift());

      if (key === name) {
        return decodeURIComponent(parts.join('='));
      }
    }

    return null;
  }

  function writeCookie(name, value, maxAgeSeconds) {
    document.cookie = name + '=' + encodeURIComponent(value) + '; ' + buildAttributes(maxAgeSeconds);
  }

  /**
   * Validates that a college code is a safe, expected token.
   * Prevents malformed or malicious cookie values from reaching config lookup.
   */
  function isValidCollegeCode(code) {
    return typeof code === 'string' && /^[a-z0-9_-]{2,40}$/.test(code);
  }

  /**
   * Sets the student session cookies (48-hour expiry).
   * @param {string} collegeCode - e.g. "ssipmt"
   */
  function set(collegeCode) {
    if (!isValidCollegeCode(collegeCode)) {
      return false;
    }

    var maxAgeSeconds = COOKIE_HOURS * 60 * 60;
    writeCookie(STUDENT_FLAG_COOKIE, 'true', maxAgeSeconds);
    writeCookie(COLLEGE_CODE_COOKIE, collegeCode, maxAgeSeconds);

    return true;
  }

  /**
   * Gets the current student college code from the cookies.
   * Returns null if missing, expired, or invalid.
   */
  function get() {
    var flag = readRawCookie(STUDENT_FLAG_COOKIE);
    if (flag !== 'true') {
      return null;
    }

    var code = readRawCookie(COLLEGE_CODE_COOKIE);
    if (!isValidCollegeCode(code)) {
      return null;
    }

    return code;
  }

  /**
   * Returns true if a valid student session cookie is present.
   */
  function has() {
    return get() !== null;
  }

  /**
   * Marks that this session was established by visiting a student
   * landing page (Condition 1 of the 4-condition render check).
   * Same 48h expiry as the session cookies.
   */
  function setLandingEntry() {
    writeCookie(LANDING_COOKIE, '1', COOKIE_HOURS * 60 * 60);
    return true;
  }

  /**
   * Returns true only if this session was established via a student
   * landing page (and hasn't expired).
   */
  function hasLandingEntry() {
    return readRawCookie(LANDING_COOKIE) === '1';
  }

  /**
   * Clears the student session immediately.
   */
  function clear() {
    document.cookie = STUDENT_FLAG_COOKIE + '=; ' + buildAttributes(0);
    document.cookie = COLLEGE_CODE_COOKIE + '=; ' + buildAttributes(0);
    document.cookie = LANDING_COOKIE + '=; ' + buildAttributes(0);
  }

  return {
    set: set,
    get: get,
    has: has,
    clear: clear,
    setLandingEntry: setLandingEntry,
    hasLandingEntry: hasLandingEntry
  };
})();
